<template>
    <!-- 新增楼子节点 -->
    <el-dialog
        :title="props.formId ? '编辑节点' : '添加节点'"
        :model-value="modelValue"
        @open="dialogOpen"
        @closed="closed"
        width="500px"
    >
        <el-form ref="formRef" :model="formData" :rules="formRules" size="default" label-width="100px">
            <el-form-item label="上级节点" prop="parent">
                <el-tree-select
                    v-model="formData.parent"
                    :data="nodeOptionLists"
                    check-strictly
                    :render-after-expand="false"
                    value-key="name"
                    node-key="id"
                    placeholder="请选择"
                    :default-expanded-keys="['']"
                    style="width: 100%"
                    :props="{ label: 'name' }"
                    @change="parentChange"
                ></el-tree-select>
            </el-form-item>
            <el-form-item label="节点类型" prop="node_type">
                <el-select v-model="formData.node_type" placeholder="请选择" disabled style="width: 100%">
                    <el-option
                        v-for="item in node_type_options"
                        :key="item.id"
                        :label="item.label"
                        :value="item.value"
                    ></el-option>
                </el-select>
            </el-form-item>
            <el-form-item label="添加方式" v-if="formData.node_type == '4' && formId === ''">
                <el-radio-group v-model="addType">
                    <el-radio :value="1">单个添加</el-radio>
                    <el-radio :value="2">批量添加</el-radio>
                </el-radio-group>
            </el-form-item>
            <el-form-item label="节点名称" prop="name" v-if="addType == 1">
                <el-input v-model="formData.name" placeholder="请输入" />
            </el-form-item>
            <el-form-item label="通讯地址" prop="address" v-if="formData.node_type === '1'">
                <el-input v-model="formData.address" placeholder="请输入" />
            </el-form-item>
            <el-form-item label="热力站名称" prop="station" v-if="formData.node_type === '1'">
                <el-input v-model="formData.station" placeholder="请输入" />
            </el-form-item>
            <el-form-item label="层高" prop="floor" v-if="addType == 2">
                <el-select v-model="formData.floor" placeholder="请选择" style="width: 100%">
                    <el-option v-for="item in 100" :key="item" :label="item" :value="item"></el-option>
                </el-select>
            </el-form-item>
            <el-form-item label="显示排序" prop="list_order">
                <el-input-number v-model="formData.list_order" class="mx-4" :min="0" :max="999999" controls-position="right" />
            </el-form-item>
        </el-form>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="closed">取消</el-button>
                <el-button type="primary" :loading="submitLoading" @click="onConfirm">确定</el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script setup>
import { defineEmits, defineProps, nextTick, onMounted, reactive, ref } from 'vue'
import { addBuilding, batchFloor, getBuildingDetail, putBuilding } from '@/api/building'
import { ElMessage } from 'element-plus'
import { getDataCodeOptions } from '@/utils/common'

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    nodeOptionLists: {
        type: Array,
        required: true
    },
    parentId: {
        type: String,
        required: true
    },
    parentNodeType: {
        type: String,
        required: true
    },
    formId: {
        type: String,
        required: false
    }
})
const emits = defineEmits(['update:modelValue', 'updateForm'])
const submitLoading = ref(false)
const formData = reactive({
    parent: '',
    node_type: '0',
    name: '',
    address: '',
    station: '',
    list_order: 1,
    status: 1,
    visible: 1
})

// 上级节点改变时，更改节点类型
function parentChange(value) {
    // 从nodeOptionLists树形数据中获取节点类型
    const findParentNodeType = (arr) => {
        arr.forEach((item) => {
            if (item.id === value) {
                formData.node_type = item.node_type + 1 + ''
            } else {
                if (item.children) {
                    findParentNodeType(item.children)
                }
            }
        })
    }
    findParentNodeType(props.nodeOptionLists)
}

const addType = ref(1)
const formRules = reactive({
    parent: [
        {
            required: true,
            trigger: 'blur',
            message: '请选择上级节点'
        }
    ],
    name: [
        {
            required: true,
            trigger: 'blur',
            message: '请输入节点名称'
        }
    ],
    node_type: [
        {
            required: true,
            trigger: 'blur',
            message: '请选择节点类型'
        }
    ],
    floor: [
        {
            required: true,
            trigger: 'blur',
            message: '请选择层高'
        }
    ]
})
const formRef = ref(null)
const onConfirm = () => {
    formRef.value.validate(async (val) => {
        if (val) {
            try {
                const params = { ...formData }
                params.level = params.node_type
                submitLoading.value = true
                if (params.node_type === '4' && props.formId === '') {
                    if (addType.value === 1) {
                        delete params.floor
                    } else if (addType.value === 2) {
                        delete params.name
                    }
                }
                if (props.formId) {
                    await putBuilding(params)
                    submitLoading.value = false
                    emits('updateForm')
                    ElMessage.success('修改成功！')
                    closed()
                } else {
                    if (addType.value === 1) {
                        // 逐个添加
                        await addBuilding(params)
                    } else {
                        // 批量添加
                        await batchFloor(params)
                    }
                    submitLoading.value = false
                    emits('updateForm')
                    ElMessage.success('添加成功！')
                    closed()
                }
            } catch (error) {
                submitLoading.value = false
            }
        }
    })
}

function dialogOpen() {
    if (props.parentId) {
        formData.parent = props.parentId
    }
    if (props.formId) {
        getBuildingDetail(props.formId).then((data) => {
            nextTick(() => {
                formData.name = data.name
                formData.id = data.id
                formData.parent = data.parent
                formData.node_type = data.node_type.toString()
                formData.address = data.address
                formData.station = data.station
                formData.list_order = data.list_order
                formData.status = data.status
            })
        })
    } else {
        formData.name = ''
        // formData.id = ''
        formData.parent = props.parentId
        formData.node_type = (Number(props.parentNodeType) + 1).toString()
        formData.address = ''
        formData.station = ''
        formData.list_order = 1
        formData.status = 1
        addType.value = 1
    }
}

const closed = () => {
    emits('update:modelValue', false)
    formRef.value.resetFields()
}

const node_type_options = ref([])
onMounted(async () => {
    const { list } = await getDataCodeOptions('building_node_type')
    node_type_options.value = list
})
</script>

<style></style>
